/**
 * New Year labels — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearLabelsPack: ToilePack = {
    slug: 'new-year-labels',
    category: 'season',
    name: 'New Year labels',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'bottle', label: 'Bottle labels', subject: 'a sheet of rectangular labels each lettered "CHEERS" with a blank line beneath, a gold border, dashed cut lines'},
        {key: 'favour', label: 'Favour labels', subject: 'a sheet of round favour labels each lettered "THANK YOU" with a confetti border and a blank centre for a name, dashed cut lines, arranged in a grid'},
        {key: 'seals', label: 'Year seals', subject: 'a sheet of round seal labels each arched with "HAPPY NEW YEAR" around a blank centre band for the year, dashed cut lines, arranged in a grid'},
    ],
    styles: [
        {key: 'glam', label: 'Black and gold', style: 'black-and-gold new year printable, confetti borders, elegant serif lettering, print-ready', palette: 'Black, gold and champagne cream', framing: 'Laid out on a square sheet with a gold border and an even margin, the labels arranged in a grid, print-ready.'},
        {key: 'confetti', label: 'Confetti party', style: 'bright confetti party printable, streamers, rounded bold lettering, print-ready', palette: 'Gold, pink, teal and white', framing: 'Laid out on a square sheet with a confetti border and a white margin, the labels arranged in a grid, print-ready.'},
        {key: 'minimal', label: 'Minimal', style: 'minimal new year printable, thin lines, generous white space, print-ready', palette: 'Black and gold on white', framing: 'Laid out on a square sheet with a wide margin, the labels arranged in a grid, print-ready.'},
    ],
    directions: ['for a bridal shower instead', 'with a monogram instead', 'as a sheet of stickers'],
    directionHint: 'try: for a bridal shower instead',
    seo: {
        title: 'New Year Labels: Bottle, Favour & Year Seals',
        metaDescription: 'Nine New Year labels — champagne bottle labels, party favour labels, and round year seals — blank centers, in black-and-gold, confetti, minimal.',
        h1: 'New Year labels: bottle, favour, and year seals',
        tagline: 'Nine sheets, three label types, three styles each — blank centers, cut lines drawn.',
        intro: "Three New Year label sheets — champagne bottle labels, party favour labels, and round year seals — each with a blank center or line left bare for a pen, in black-and-gold, confetti-party, and minimal styles. Ask for a different bottle shape or a couple's-names version, and the same label grammar carries it.",
        useCases: [
            "New Year's Eve party favors",
            'a champagne toast station',
            'gift tags for party guests',
            'a countdown party goodie bag',
        ],
        faq: [
            {q: 'Are the centers pre-printed with a name, or blank?', a: 'Blank — every center is left bare in the image so you can write a name or message by hand after printing.'},
            {q: 'What size do these print at?', a: 'Each sheet is a single page sized close to a standard label-sheet layout.'},
            {q: 'Can I get a different bottle shape?', a: 'Yes — the direction field takes any variation, including a different bottle shape.'},
            {q: 'What are the three styles?', a: 'Black-and-gold, bright confetti-party, and minimal thin-line.'},
        ],
    },
}
